import {
  AttachMoney,
  Calculate,
  Equalizer,
  RequestQuote,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { getFundSummary } from 'lib/api/multiverse/fund';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS, CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

const DATA_HEADERS = [
  { key: 'segmentName', label: 'Segmento', type: 'money' },
  { key: 'noArrear', label: 'Sin mora', type: 'money' },
  { key: 'days1to30', label: '1-30 días', type: 'money' },
  { key: 'days31to60', label: '31-60 días', type: 'money' },
  { key: 'days61to90', label: '61-90 días', type: 'money' },
  { key: 'days91Plus', label: '91+ días', type: 'money' },
];

const TOGGLE_BUTTON_OPTIONS = [
  { value: 'economic', label: 'Económico', ariaLabel: 'economic' },
  { value: 'sales', label: 'Venta', ariaLabel: 'sales' },
];

type ArrearType = 'economic' | 'sales';

const Summary = () => {
  const dispatch = useDispatch();
  const [clientChartType, setClientChartType] =
    useState<ArrearType>('economic');
  const [stakeholderChartType, setStakeholderChartType] =
    useState<ArrearType>('economic');
  const [clientArrearType, setClientArrearType] =
    useState<ArrearType>('economic');
  const [stakeholderArrearType, setStakeholderArrearType] =
    useState<ArrearType>('economic');

  const { data, isPending } = getFundSummary(dispatch);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  const sortedPieChartData = [...(data?.cessionAmountPieChartData || [])].sort(
    (a, b) => a.name.localeCompare(b.name),
  );

  const lineChartData = data?.cessionAmountLineChartData || [];
  const sortedLineChartData =
    lineChartData.length > 0
      ? lineChartData.map((item) => {
          const sortedKeys = Object.keys(item)
            .filter((key) => key !== 'name')
            .sort();
          const sortedItem: { name: string; [key: string]: string | number } = {
            name: item.name,
          };
          sortedKeys.forEach((key) => {
            sortedItem[key] = item[key];
          });
          return sortedItem;
        })
      : [];

  const allBusinessNames = new Set<string>();
  if (lineChartData.length > 0) {
    Object.keys(lineChartData[0]).forEach((key) => {
      if (key !== 'name') allBusinessNames.add(key);
    });
  }
  sortedPieChartData.forEach((item) => allBusinessNames.add(item.name));

  const businessColorMap = new Map<string, string>();
  const sortedNames = Array.from(allBusinessNames).sort();
  const colors = Object.values(CHART_COLORS);
  sortedNames.forEach((name, index) => {
    businessColorMap.set(name, colors[index % colors.length]);
  });

  const lineChartColors =
    lineChartData.length > 0
      ? Object.keys(lineChartData[0])
          .filter((key) => key !== 'name')
          .sort()
          .map((key) => businessColorMap.get(key) || CHART_COLORS.blue)
      : [];

  const pieChartColors = sortedPieChartData
    .map((item) => item.name)
    .sort()
    .map((name) => businessColorMap.get(name) || CHART_COLORS.blue);

  return (
    <Grid container spacing={2} my={1}>
      <Grid item xs={8}>
        <TableCard>
          <SubtitleWithTooltip
            title="Cesión en el tiempo"
            tooltipContent="Estos son los montos de cesión por fuente de financiamiento a lo largo del tiempo."
            tooltipPlacement="right"
            variant="compact"
          />
          <LineChart
            key="fundCessionAmountLineChart"
            data={sortedLineChartData}
            colors={lineChartColors}
            showLegend={true}
            height={290}
            showArea={false}
          />
        </TableCard>
      </Grid>

      <Grid item xs={4}>
        <TableCard>
          <SubtitleWithTooltip
            title="Monto de cesión"
            tooltipContent="Estos son los montos de cesión por fuente de financiamiento"
            tooltipPlacement="right"
            variant="compact"
          />
          <CentralPieChart data={sortedPieChartData} colors={pieChartColors} />
        </TableCard>
      </Grid>

      <Grid item xs={3}>
        <StatCard
          label="Tasa promedio"
          value={(data?.statistics.averageRate ?? 0).toFixed(2)}
          valueLabel="%"
          color={BASE_COLORS.blue}
          icon={<RequestQuote sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Promedio cesión"
          value={formatMoney(data?.statistics.averageCessionAmount || 0)}
          valueLabel="CLP"
          color={BASE_COLORS.green}
          icon={<AttachMoney sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Promedio operación"
          value={formatMoney(
            data?.statistics.averageCessionAmountByNominee || 0,
          )}
          valueLabel="CLP"
          color={BASE_COLORS.purple}
          icon={<Calculate sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Financiamiento promedio"
          value={data?.statistics.averageAdvance || 0}
          valueLabel="%"
          color={BASE_COLORS.red}
          icon={<Equalizer sx={{ fontSize: 24 }} />}
        />
      </Grid>

      <Grid item xs={6}>
        <TableCard>
          <SubtitleWithTooltip
            title="Clientes"
            tooltipContent="Desglose de cesión por segmento económico o de venta del cliente"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={clientChartType}
              onChange={(value) =>
                setClientChartType(value as 'economic' | 'sales')
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <CentralPieChart
            data={
              clientChartType === 'economic'
                ? data?.clientEconomicSegmentPieChartData || []
                : data?.clientSalesSegmentPieChartData || []
            }
            height={240}
          />
        </TableCard>
      </Grid>
      <Grid item xs={6}>
        <TableCard>
          <SubtitleWithTooltip
            title="Deudores"
            tooltipContent="Desglose de cesión por segmento económico o de venta del deudor"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={stakeholderChartType}
              onChange={(value) =>
                setStakeholderChartType(value as 'economic' | 'sales')
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <CentralPieChart
            data={
              stakeholderChartType === 'economic'
                ? data?.stakeholderEconomicSegmentPieChartData || []
                : data?.stakeholderSalesSegmentPieChartData || []
            }
            height={240}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Clientes"
            tooltipContent="Desglose de mora por segmento económico o de venta del cliente"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={clientArrearType}
              onChange={(value) =>
                setClientArrearType(value as 'economic' | 'sales')
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <EnhancedTable
            data={
              clientArrearType === 'economic'
                ? data?.clientEconomicSegmentArrearTable || []
                : data?.clientSalesSegmentArrearTable || []
            }
            dataHeaders={DATA_HEADERS}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Deudores"
            tooltipContent="Desglose de mora por segmento económico o de venta del deudor"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={stakeholderArrearType}
              onChange={(value) =>
                setStakeholderArrearType(value as 'economic' | 'sales')
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <EnhancedTable
            data={
              stakeholderArrearType === 'economic'
                ? data?.stakeholderEconomicSegmentArrearTable || []
                : data?.stakeholderSalesSegmentArrearTable || []
            }
            dataHeaders={DATA_HEADERS}
          />
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default Summary;
